import { useEffect, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import {
  DesktopDialog,
  HubSheet,
  HubSheetActions,
} from "../AdminPortal/HubPwa";
import aa from "../AdminPortal/adminApp.module.css";
import s from "../clinic.module.css";
import { Button, Field, Input, Select, useToast } from "../../ui";
import { toDateInputValue } from "../../../utils/appointmentLabels";
import { MoreMenu } from "./AcceptMembershipPayment";

function amountFromAttendance(attendance) {
  if (
    attendance?.session_fee_amount != null &&
    Number(attendance.session_fee_amount) > 0
  ) {
    return String(Math.round(Number(attendance.session_fee_amount)));
  }
  return "";
}

function dueAmount(attendance) {
  const n = Number(attendance?.session_fee_amount);
  if (Number.isFinite(n) && n > 0) return n;
  return 0;
}

function fmtRub(n) {
  return `${Math.round(Number(n) || 0).toLocaleString("ru-RU")} ₽`;
}

/**
 * Разовое занятие: в финансах — как абонемент (принять / изменить / списать),
 * в журнале — компактный чип + лист.
 *
 * @param {"finance"|"journal"} variant
 */
export default function AcceptSessionFeePayment({
  attendance,
  onSuccess,
  variant = "journal",
  size = "sm",
}) {
  const toast = useToast();
  const isMobile = useIsMobile(768);
  const isFinance = variant === "finance";
  const [mode, setMode] = useState("idle"); // idle | pay | editDebt | sheet
  const [busy, setBusy] = useState(false);
  const [method, setMethod] = useState("cash");
  const [amount, setAmount] = useState(() => amountFromAttendance(attendance));
  const [debtEdit, setDebtEdit] = useState(() => amountFromAttendance(attendance));
  const [paidDate, setPaidDate] = useState(() => toDateInputValue());

  useEffect(() => {
    if (mode === "pay" || mode === "sheet") {
      setAmount(amountFromAttendance(attendance));
      setMethod("cash");
      setPaidDate(toDateInputValue());
    }
    if (mode === "editDebt") {
      setDebtEdit(amountFromAttendance(attendance) || "0");
    }
  }, [mode, attendance?.id, attendance?.session_fee_amount]);

  if (!attendance || attendance.session_fee_status !== "due") return null;

  const due = dueAmount(attendance);
  const feeLabel = due > 0 ? Math.round(due).toLocaleString("ru-RU") : null;
  const name = attendance.patient?.full_name || "Ученик";

  const parsePositive = (raw, emptyMsg = "Укажите сумму") => {
    const sum = Number(String(raw).replace(",", "."));
    if (!Number.isFinite(sum) || sum <= 0) {
      toast.error(emptyMsg);
      return null;
    }
    return sum;
  };

  const closeSheet = () => {
    if (!busy) setMode("idle");
  };

  const acceptPay = async (e) => {
    e?.preventDefault?.();
    const sum = parsePositive(amount, "Укажите сумму за это занятие");
    if (sum == null) return;
    if (!paidDate) {
      toast.error("Укажите дату оплаты");
      return;
    }
    const paidAt = new Date(`${paidDate}T12:00:00`);
    if (Number.isNaN(paidAt.getTime())) {
      toast.error("Некорректная дата оплаты");
      return;
    }
    setBusy(true);
    try {
      const data = await clinicAPI.createSessionFeePayment({
        attendance_id: attendance.id,
        amount: sum,
        method,
        paid_at: paidAt.toISOString(),
      });
      toast.success("Оплата за занятие принята");
      setMode("idle");
      onSuccess?.(data);
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось записать оплату");
    } finally {
      setBusy(false);
    }
  };

  const saveAmountForOnline = async () => {
    const sum = parsePositive(amount, "Укажите сумму за это занятие");
    if (sum == null) return;
    setBusy(true);
    try {
      const data = await clinicAPI.updateSessionFeeAmount({
        attendance_id: attendance.id,
        amount: sum,
      });
      toast.success("Сумма сохранена — клиент может оплатить онлайн");
      setMode("idle");
      onSuccess?.(data);
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось сохранить сумму");
    } finally {
      setBusy(false);
    }
  };

  const forgiveDebt = async () => {
    if (
      !window.confirm(
        "Списать долг без оплаты? Клиент исчезнет из должников, в выручку ничего не попадёт.",
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      const data = await clinicAPI.waiveSessionFee({
        attendance_id: attendance.id,
      });
      toast.success("Долг списан");
      setMode("idle");
      onSuccess?.(data);
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось списать долг");
    } finally {
      setBusy(false);
    }
  };

  const saveDebtEdit = async (e) => {
    e.preventDefault();
    const newDue = Number(String(debtEdit).replace(",", "."));
    if (!Number.isFinite(newDue) || newDue < 0) {
      toast.error("Укажите сумму долга от 0");
      return;
    }
    setBusy(true);
    try {
      if (newDue <= 0) {
        const data = await clinicAPI.waiveSessionFee({
          attendance_id: attendance.id,
        });
        toast.success("Долг обнулён");
        setMode("idle");
        onSuccess?.(data);
        return;
      }
      const data = await clinicAPI.updateSessionFeeAmount({
        attendance_id: attendance.id,
        amount: newDue,
      });
      toast.success(`Долг обновлён: ${fmtRub(newDue)}`);
      setMode("idle");
      onSuccess?.(data);
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось изменить долг");
    } finally {
      setBusy(false);
    }
  };

  const moreItems = [
    {
      id: "edit-debt",
      label: "Изменить сумму долга",
      onClick: () => setMode("editDebt"),
    },
    {
      id: "forgive",
      label: "Списать долг",
      onClick: forgiveDebt,
    },
  ];

  /* ── Финансы: тот же UX, что у абонементов ── */
  if (isFinance) {
    if (mode === "editDebt") {
      return (
        <form className={s.membershipDebtBox} onSubmit={saveDebtEdit}>
          <p className={s.membershipDebtBoxTitle}>Сумма долга</p>
          <p className={s.membershipDebtBoxHint}>
            Разовое занятие. Укажите сумму к оплате или 0, чтобы списать.
          </p>
          <label className={s.membershipPayField}>
            <span className={s.membershipPayLabel}>Долг, ₽</span>
            <Input
              type="number"
              min={0}
              step={1}
              inputMode="numeric"
              value={debtEdit}
              onChange={(e) => setDebtEdit(e.target.value)}
              disabled={busy}
            />
          </label>
          <div className={s.membershipPayFormBtns}>
            <Button type="submit" size={size} disabled={busy}>
              {busy ? "…" : "Сохранить"}
            </Button>
            <Button
              type="button"
              size={size}
              variant="secondary"
              disabled={busy}
              onClick={() => setMode("idle")}
            >
              Назад
            </Button>
          </div>
        </form>
      );
    }

    if (mode === "pay") {
      return (
        <form className={s.membershipDebtBox} onSubmit={acceptPay}>
          <p className={s.membershipDebtBoxTitle}>
            {due > 0 ? `К оплате ${fmtRub(due)}` : "Записать оплату"}
          </p>
          <p className={s.membershipDebtBoxHint}>Разовое посещение</p>
          <label className={s.membershipPayField}>
            <span className={s.membershipPayLabel}>Принимаете сейчас, ₽</span>
            <Input
              type="number"
              min={1}
              step={1}
              inputMode="numeric"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={busy}
            />
          </label>
          <label className={s.membershipPayField}>
            <span className={s.membershipPayLabel}>Способ</span>
            <Select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              disabled={busy}
            >
              <option value="cash">Наличные</option>
              <option value="card_transfer">Перевод</option>
              <option value="other">Другое</option>
            </Select>
          </label>
          <label className={s.membershipPayField}>
            <span className={s.membershipPayLabel}>Дата оплаты</span>
            <Input
              type="date"
              value={paidDate}
              max={toDateInputValue()}
              onChange={(e) => setPaidDate(e.target.value)}
              disabled={busy}
              required
            />
          </label>
          <div className={s.membershipPayFormBtns}>
            <Button type="submit" size={size} disabled={busy}>
              {busy ? "…" : "Принять оплату"}
            </Button>
            <Button
              type="button"
              size={size}
              variant="secondary"
              disabled={busy}
              onClick={() => setMode("idle")}
            >
              Назад
            </Button>
          </div>
        </form>
      );
    }

    return (
      <div className={s.membershipDebtBox}>
        <div className={s.membershipDebtBoxHead}>
          <strong className={s.membershipDebtBoxSum}>
            {due > 0 ? `Долг ${fmtRub(due)}` : "Долг без суммы"}
          </strong>
          <span className={s.membershipDebtBoxHint}>Разовое занятие</span>
        </div>
        <div className={s.membershipActionBar}>
          <Button
            type="button"
            size={size}
            onClick={() => {
              setAmount(amountFromAttendance(attendance));
              setMode("pay");
            }}
          >
            Принять оплату
          </Button>
          <MoreMenu items={moreItems} size={size} disabled={busy} />
        </div>
      </div>
    );
  }

  /* ── Журнал: чип + лист ── */
  const sheetOpen = mode === "sheet";
  const Sheet = isMobile ? HubSheet : DesktopDialog;
  const formId = "journal-session-fee-form";

  return (
    <>
      <button
        type="button"
        className={aa.journalSessionFeeChip}
        onClick={(e) => {
          e.stopPropagation();
          setMode("sheet");
        }}
      >
        <span className={aa.journalSessionFeeChipLabel}>
          {feeLabel ? `К оплате ${feeLabel} ₽` : "Указать сумму"}
        </span>
        <span className={aa.journalSessionFeeChipAction}>Оплатить</span>
      </button>

      <Sheet
        open={sheetOpen}
        onClose={closeSheet}
        title="Оплата за занятие"
        subtitle={name}
        titleId="journal-session-fee-title"
        bodyClassName={aa.journalSessionFeeSheetBody}
        sheetClassName={aa.journalSessionFeeDialog}
        dialogClassName={aa.journalSessionFeeDialog}
        footer={
          <HubSheetActions
            primary={busy ? "…" : "Принять оплату"}
            primaryForm={formId}
            primaryType="submit"
            primaryDisabled={busy}
            secondaryLabel="Сохранить для онлайн"
            onSecondary={() => {
              if (!busy) saveAmountForOnline();
            }}
          />
        }
      >
        <form
          id={formId}
          className={aa.journalSessionFeeForm}
          onSubmit={acceptPay}
        >
          <p className={aa.journalSessionFeeLead}>
            Разовое посещение. Можно изменить сумму, принять оплату или списать
            долг без оплаты.
          </p>
          <Field label="Сумма, ₽">
            <Input
              type="number"
              min="1"
              step="1"
              inputMode="numeric"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Например 900"
              required
              autoFocus
              disabled={busy}
            />
          </Field>
          <Field label="Способ оплаты">
            <Select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              disabled={busy}
            >
              <option value="cash">Наличные</option>
              <option value="card_transfer">Перевод</option>
              <option value="other">Другое</option>
            </Select>
          </Field>
          <Field label="Дата оплаты">
            <Input
              type="date"
              value={paidDate}
              max={toDateInputValue()}
              onChange={(e) => setPaidDate(e.target.value)}
              disabled={busy}
              required
            />
          </Field>
          <button
            type="button"
            className={aa.journalSessionFeeWaive}
            onClick={forgiveDebt}
            disabled={busy}
          >
            Списать долг
          </button>
        </form>
      </Sheet>
    </>
  );
}
